import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { useTranslation } from "react-i18next";

interface FileExplorerMultiSelectToggleProps {
  multiSelect: boolean;
  onChange: (multiSelect: boolean) => void;
  selectedCount: number;
  onClear: () => void;
}

/**
 * Labelled on purpose — the old control swapped `InsertPhotoOutlined` for the
 * near-identical `CollectionsOutlined` and communicated almost nothing. When
 * active it becomes a live count with a clear action.
 */
export default function FileExplorerMultiSelectToggle(
  props: FileExplorerMultiSelectToggleProps
) {
  const { multiSelect, onChange, selectedCount, onClear } = props;
  const { t } = useTranslation();

  // 30px / 6px radius / neutral border, matching the sort control and view
  // switcher it sits between.
  if (!multiSelect) {
    return (
      <button
        type="button"
        aria-pressed={false}
        onClick={() => onChange(true)}
        data-testid="file-explorer-multiselect-toggle"
        className="h-[30px] pl-[10px] pr-[11px] rounded-[6px] border border-creator-border bg-creator-surface flex items-center gap-[7px] text-[12px] leading-[18px] font-medium text-creator-text hover:bg-creator-childActive transition-colors whitespace-nowrap"
      >
        <span
          aria-hidden
          className="w-[12px] h-[12px] rounded-[3px] border-[1.2px] border-creator-text-sub"
        />
        {t("file_explorer_ui.select_multiple")}
      </button>
    );
  }

  return (
    <div
      data-testid="file-explorer-multiselect-toggle"
      className="h-[30px] flex items-center rounded-[6px] border border-creator-primary overflow-hidden"
    >
      <button
        type="button"
        aria-pressed={true}
        onClick={() => onChange(false)}
        className="h-full pl-[10px] pr-[11px] flex items-center gap-[7px] text-[12px] leading-[18px] whitespace-nowrap bg-creator-primary-washed text-creator-text-key font-medium"
      >
        <span
          aria-hidden
          className="w-[12px] h-[12px] rounded-[3px] border-[1.2px] border-creator-primary bg-creator-primary flex items-center justify-center text-white text-[9px] leading-none"
        >
          ✓
        </span>
        {selectedCount > 0
          ? t("file_explorer_ui.selected_count", { count: selectedCount })
          : t("file_explorer_ui.select_multiple")}
      </button>
      <button
        type="button"
        aria-label={t("file_explorer_ui.clear_selection") ?? ""}
        onClick={onClear}
        data-testid="file-explorer-multiselect-clear"
        className="h-full px-[6px] text-[14px] leading-none text-creator-text-sub hover:bg-creator-childActive border-l border-creator-border flex items-center"
      >
        <CloseOutlinedIcon fontSize="inherit" />
      </button>
    </div>
  );
}
